// File: app/login/page.tsx
"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useUserContext } from "../contexts/UserContext";
import validator from "validator";
import { motion } from "framer-motion";
import Image from "next/image";
import { FaDownload, FaSignInAlt } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import Link from "next/link";
import ThemeToggle from "@/components/ThemeToggle";

const OTP_VALIDITY_MS = 2 * 60 * 1000; // ۲ دقیقه اعتبار کد
const BLOCK_MS = 30 * 1000; // ۳۰ ثانیه بلوک پس از تلاش ناموفق
const MAX_LOGIN_FAIL = 3;
const MAX_WRONG_OTP = 10;
const BAZAAR_APP_URL =
  "https://cafebazaar.ir/app/com.bayerkashan.webviewapp";
const BAZAAR_INTENT_URL =
  "intent://details?id=com.bayerkashan.webviewapp#Intent;scheme=bazaar;package=com.farsitel.bazaar;S.browser_fallback_url=https%3A%2F%2Fcafebazaar.ir%2Fapp%2Fcom.bayerkashan.webviewapp;end";

/** تبدیل ارقام فارسی/عربی به لاتین */
function normalizeDigits(input: string): string {
  if (!input) return "";
  const map: Record<string, string> = {
    "۰": "0",
    "۱": "1",
    "۲": "2",
    "۳": "3",
    "۴": "4",
    "۵": "5",
    "۶": "6",
    "۷": "7",
    "۸": "8",
    "۹": "9",
    "٠": "0",
    "١": "1",
    "٢": "2",
    "٣": "3",
    "٤": "4",
    "٥": "5",
    "٦": "6",
    "٧": "7",
    "٨": "8",
    "٩": "9",
  };
  return String(input || "").replace(/[۰-۹٠-٩]/g, (d) => map[d] ?? d);
}

/** فرمت mm:ss برای شمارش معکوس */
function formatMs(ms: number) {
  const m = Math.floor(ms / 60000)
    .toString()
    .padStart(2, "0");
  const s = Math.floor((ms % 60000) / 1000)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

/** تشخیص وجود حروف فارسی در رمز عبور */
const hasPersian = (text: string) => /[\u0600-\u06FF]/.test(text);

/** ورود فقط با شماره موبایل: 09…، 9…، 98… و +98… به شکلِ ۱۱ رقمیِ 09 تبدیل می‌شود. */
function normalizeLoginMobile(rawInput: string): string | null {
  const digits = normalizeDigits(rawInput.trim()).replace(/\D/g, "");
  if (/^09\d{9}$/.test(digits)) return digits;
  if (/^9\d{9}$/.test(digits)) return `0${digits}`;
  if (/^989\d{9}$/.test(digits)) return `0${digits.slice(2)}`;
  if (/^00989\d{9}$/.test(digits)) return `0${digits.slice(4)}`;
  return null;
}

const LoginPage: React.FC = () => {
  const [mobilePhone, setMobilePhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useUserContext();
  const router = useRouter();

  // شمارش معکوس بر اساس زمان حال
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // مقادیر ذخیره‌شده در localStorage
  const [loginFailCount, setLoginFailCount] = useState<number>(0);
  const [loginBlockedUntil, setLoginBlockedUntil] = useState<number>(0);
  const [sentAt, setSentAt] = useState<number>(0);
  const [otpVerifyBlockedUntil, setOtpVerifyBlockedUntil] =
    useState<number>(0);
  const [wrongOtpCount, setWrongOtpCount] = useState<number>(0);
  const [passwordLangError, setPasswordLangError] = useState(false);

  // بارگذاری مقادیر از localStorage (فقط در کلاینت)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const lf = localStorage.getItem("loginFailCount");
    setLoginFailCount(lf ? Number(lf) : 0);

    const lb = localStorage.getItem("loginBlockedUntil");
    setLoginBlockedUntil(lb ? Number(lb) : 0);

    const sa = localStorage.getItem("otpSentAt");
    setSentAt(sa ? Number(sa) : 0);

    const ov = localStorage.getItem("otpVerifyBlockedUntil");
    setOtpVerifyBlockedUntil(ov ? Number(ov) : 0);

    const wo = localStorage.getItem("wrongOtpCount");
    setWrongOtpCount(wo ? Number(wo) : 0);
  }, []);

  // محاسبه زمان باقی‌مانده
  const loginRemaining = Math.max(0, loginBlockedUntil - now);
  const otpValidUntil = sentAt + OTP_VALIDITY_MS;
  const otpRemaining = Math.max(0, otpValidUntil - now);
  const otpVerifyRemaining = Math.max(0, otpVerifyBlockedUntil - now);

  const resetLoginBlock = () => {
    setLoginFailCount(0);
    setLoginBlockedUntil(0);
    localStorage.removeItem("loginFailCount");
    localStorage.removeItem("loginBlockedUntil");
  };

  // روی اندروید مستقیم اپ بازار باز شود؛ در غیر این صورت صفحه‌ی اپ در سایت بازار
  const handleBazaarDownloadClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (typeof window === "undefined") return;

    if (/Android/i.test(window.navigator.userAgent)) {
      e.preventDefault();
      window.location.href = BAZAAR_INTENT_URL;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (loginRemaining > 0) {
      setError(`لطفاً پس از ${formatMs(loginRemaining)} مجدداً تلاش کنید.`);
      return;
    }

    if (!mobilePhone.trim()) {
      setError("شماره موبایل را وارد کنید.");
      return;
    }

    const normalizedPhone = normalizeLoginMobile(mobilePhone);
    if (!normalizedPhone || !validator.isMobilePhone(normalizedPhone, "fa-IR")) {
      setError("ورود فقط با شماره موبایل ثبت‌شده امکان‌پذیر است؛ شماره ۱۱ رقمی را با 09 وارد کنید.");
      return;
    }

    if (password.length < 4 || password.length > 100) {
      setError("رمز عبور باید بین ۴ تا ۱۰۰ کاراکتر باشد.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: normalizedPhone,
          mobilePhone: normalizedPhone,
          password,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        const newCount = loginFailCount + 1;
        setLoginFailCount(newCount);
        localStorage.setItem("loginFailCount", newCount.toString());
        if (newCount >= MAX_LOGIN_FAIL) {
          const until = Date.now() + BLOCK_MS;
          setLoginBlockedUntil(until);
          localStorage.setItem("loginBlockedUntil", until.toString());
        }
        setError(data.message || "شماره موبایل یا رمز عبور اشتباه است.");
      } else if (data.step === "otp") {
        setOtpStep(true);
        resetLoginBlock();
        const nowTs = Date.now();
        setSentAt(nowTs);
        localStorage.setItem("otpSentAt", nowTs.toString());
        setOtpVerifyBlockedUntil(0);
        localStorage.removeItem("otpVerifyBlockedUntil");
        setWrongOtpCount(0);
        localStorage.removeItem("wrongOtpCount");
      } else {
        resetLoginBlock();
        login(data.role, data.employeeCode, data.user);

        const nextPath =
          typeof data?.nextPath === "string" && data.nextPath
            ? data.nextPath
            : data.role === "admin"
            ? "/employee/dashboard-admin"
            : "/employee/dashboard";

        router.push(nextPath);
      }
    } catch {
      setError("خطا در ارتباط با سرور.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (otpVerifyRemaining > 0) {
      setError(
        `لطفاً پس از ${formatMs(otpVerifyRemaining)} دوباره تلاش کنید.`
      );
      return;
    }

    if (!otp.trim()) {
      setError("کد اشتباه است.");
      const until = Date.now() + BLOCK_MS;
      setOtpVerifyBlockedUntil(until);
      localStorage.setItem("otpVerifyBlockedUntil", until.toString());
      return;
    }

    const normalizedPhone = normalizeLoginMobile(mobilePhone);

    if (!normalizedPhone) {
      setError("شماره موبایل معتبر نیست. لطفاً دوباره وارد شوید.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/login/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobilePhone: normalizedPhone,
          otp: otp.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const newWrong = wrongOtpCount + 1;
        setWrongOtpCount(newWrong);
        localStorage.setItem("wrongOtpCount", newWrong.toString());
        if (newWrong >= MAX_WRONG_OTP) {
          localStorage.removeItem("wrongOtpCount");
          window.location.reload();
          return;
        }
        setError(data.message || "کد اشتباه است.");
      } else {
        localStorage.removeItem("otpSentAt");
        localStorage.removeItem("otpVerifyBlockedUntil");
        localStorage.removeItem("wrongOtpCount");
        setOtpStep(false);
        login(data.role, data.employeeCode, data.user);

        const nextPath =
          typeof data?.nextPath === "string" && data.nextPath
            ? data.nextPath
            : "/employee/dashboard";

        router.push(nextPath);
      }
    } catch {
      setError("خطا در ارتباط با سرور.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-login-page className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-slate-50 flex items-center justify-center px-4 py-10">
      <div className="fixed left-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <div className="max-w-md w-full space-y-8">
        {/* هدر شبیه صفحه اصلی */}
        <header className="text-center space-y-4">
        </header>

        {/* کارت لاگین */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="bg-slate-900/80 border border-slate-700 rounded-2xl shadow-2xl shadow-black/50 backdrop-blur-xl p-6 md:p-7 space-y-5"
        >
          {/* لوگوی افقیِ شرکت (۱۴۴۱×۵۳۱) با همان نسبت، روی صفحه‌ی سفید تا آبی و نارنجیِ لوگو در هر سه تم خوانا باشد */}
          <div className="mb-2 flex justify-center">
            <div className="rounded-2xl bg-white px-6 py-3 shadow-lg shadow-black/25 ring-1 ring-black/5">
              <Image
                src="/login/logo.png"
                alt="ریسندگی بایر کاشان"
                width={1441}
                height={531}
                priority
                className="h-auto w-48 sm:w-56"
              />
            </div>
          </div>

          <h2 className="text-xl md:text-2xl font-extrabold text-center text-slate-50">
            {otpStep ? "وارد کردن کد تایید" : "ورود به سامانه حقوق "}
          </h2>
          <div className="mx-auto h-1 w-16 rounded-full bg-gradient-to-l from-brand-500 to-brand-orange-500" />

          {!otpStep ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="tel"
                placeholder="شماره موبایل (مثلاً 09121234567)"
                aria-label="شماره موبایل"
                autoComplete="tel"
                value={mobilePhone}
                onChange={(e) => setMobilePhone(e.target.value)}
                dir="ltr"
                inputMode="tel"
                maxLength={16}
                className="w-full p-3 rounded-xl border border-slate-600 bg-slate-900/60 text-slate-50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-400 text-left"
              />

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="رمز عبور"
                  value={password}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (hasPersian(v)) {
                      setPasswordLangError(true);
                    } else {
                      setPasswordLangError(false);
                    }
                    setPassword(v);
                  }}
                  dir="ltr"
                  inputMode="text"
                  autoComplete="current-password"
                  className={`w-full p-3 pr-11 pl-3 rounded-xl border bg-slate-900/60 text-slate-50 placeholder-slate-400 focus:outline-none focus:ring-2 ${
                    passwordLangError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-slate-600 focus:ring-brand-500 focus:border-brand-400"
                  }`}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-200"
                >
                  {showPassword ? (
                    <FiEye className="w-5 h-5" />
                  ) : (
                    <FiEyeOff className="w-5 h-5" />
                  )}
                </button>
              </div>

              {passwordLangError && (
                <p className="text-red-400 text-xs text-center mt-1">
                  لطفاً صفحه‌کلید را انگلیسی کنید. رمز عبور نباید شامل حروف فارسی باشد.
                </p>
              )}

              {loginRemaining > 0 && (
                <p className="text-slate-300 text-xs text-center">
                  لطفاً پس از {formatMs(loginRemaining)} مجدداً تلاش کنید.
                </p>
              )}

              {error && (
                <p className="text-red-400 text-sm text-center">
                  {error}
                </p>
              )}

              <motion.button
                type="submit"
                disabled={loading || loginRemaining > 0 || passwordLangError}
                whileTap={{
                  scale: !loading && loginRemaining === 0 ? 0.96 : 1,
                }}
                whileHover={{
                  scale: !loading && loginRemaining === 0 ? 1.02 : 1,
                }}
                className={`w-full flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm md:text-base font-bold transition-all
                  bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30
                  hover:bg-brand-500 active:bg-brand-600
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
                  ${
                    loading || loginRemaining > 0 || passwordLangError
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }
                `}
              >
                <FaSignInAlt className="text-lg" />
                <span>ورود</span>
              </motion.button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="w-full p-3 rounded-xl bg-slate-800/70 text-slate-100 text-center text-sm border border-slate-600">
                <p>کد تأیید برای شما ارسال شده است.</p>
                <p>لطفاً کد را در کادر زیر وارد کنید.</p>
              </div>

              <input
                type="text"
                placeholder="کد تایید"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                dir="ltr"
                inputMode="numeric"
                className="w-full p-3 rounded-xl border border-slate-600 bg-slate-900/60 text-slate-50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-400 text-center"
              />

              <button
                type="button"
                onClick={handleLogin}
                disabled={loading || otpRemaining > 0}
                className={`w-full rounded-xl px-5 py-3 text-sm md:text-base font-bold transition-all
                  border
                  ${
                    otpRemaining > 0
                      ? "bg-slate-800/70 border-slate-600 text-slate-300 cursor-not-allowed"
                      : "bg-slate-800/80 hover:bg-slate-700 border-slate-600 text-slate-100"
                  }
                `}
              >
                {otpRemaining > 0 ? formatMs(otpRemaining) : "درخواست مجدد کد"}
              </button>

              {otpVerifyRemaining > 0 && (
                <p className="text-slate-300 text-xs text-center">
                  لطفاً پس از {formatMs(otpVerifyRemaining)} مجدداً تلاش کنید.
                </p>
              )}

              {error && (
                <p className="text-red-400 text-sm text-center">
                  {error}
                </p>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: !loading ? 0.96 : 1 }}
                whileHover={{ scale: !loading ? 1.02 : 1 }}
                className={`w-full rounded-xl px-5 py-3 text-sm md:text-base font-bold transition-all
                  bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30
                  hover:bg-brand-500 active:bg-brand-600
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
                  ${loading ? "opacity-60 cursor-not-allowed" : ""}
                `}
              >
                تایید کد و ورود
              </motion.button>
            </form>
          )}

          <a
            href={BAZAAR_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBazaarDownloadClick}
            className="w-full mt-3 flex min-h-12 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold text-white transition-all
              bg-[#0ea960] border border-[#18c878] shadow-lg shadow-[#0ea960]/25
              hover:bg-[#0c9957] active:bg-[#087a47]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18c878] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
              sm:text-base"
            aria-label="دانلود اپلیکیشن از بازار"
          >
            <FaDownload className="text-base sm:text-lg" aria-hidden="true" />
            <span className="leading-6">دانلود اپلیکیشن از بازار</span>
          </a>

          {/* دکمه بازگشت به صفحه اصلی */}
          <button
            type="button"
            onClick={() => router.push("/")}
            className="w-full mt-3 rounded-xl px-5 py-3 text-sm md:text-base font-bold transition-all
              bg-slate-800/70 hover:bg-slate-700 text-slate-100 border border-slate-600
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
            "
          >
            بازگشت به صفحه اصلی
          </button>

          {!otpStep && (
            <div className="flex justify-center mt-3 text-xs md:text-sm text-slate-300">
              <Link
                href="/login/forgot-password"
                className="hover:text-brand-400 transition-colors"
              >
                رمز عبور را فراموش کرده‌ام.
              </Link>
            </div>
          )}
        </motion.section>
      </div>
    </main>
  );
};

export default LoginPage;
